import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EpisodeHero } from "@/components/EpisodeHero";
import { EpisodeCard } from "@/components/EpisodeCard";
import { ShareButton } from "@/components/ShareButton";
import { Skeleton } from "@/components/ui/skeleton";
import { RelatedArticles } from "@/components/articles/ArticleBody";

const setMeta = (prop, content) => {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
};

export default function EpisodePage() {
  const { id } = useParams();
  const [ep, setEp] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setEp(null); setError(false);
    api.get(`/episodes/${id}`).then((r) => setEp(r.data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    if (!ep) return;
    document.title = `${ep.title} · ${ep.show?.name || "Underdog Podcasts"}`;
    setMeta("og:title", ep.title); setMeta("og:description", ep.summary); setMeta("og:image", ep.image_url || ep.show?.cover_url); setMeta("og:url", window.location.href);
    return () => { document.title = "Underdog Podcasts"; };
  }, [ep]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="episode-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-8 pb-32">
        {ep?.show ? (
          <Link to={`/shows/${ep.show.slug}`} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="episode-back-link"><ArrowLeft size={16} /> All {ep.show.kind === "audio" ? "episodes" : "videos"} of {ep.show.name}</Link>
        ) : (
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="episode-back-link"><ArrowLeft size={16} /> Back to home</Link>
        )}
        {error ? (
          <div className="card-surface rounded-3xl p-12 text-center text-slate-400" data-testid="episode-not-found">Episode not found.</div>
        ) : !ep ? (
          <Skeleton className="h-96 rounded-3xl bg-white/5" />
        ) : (
          <>
            <EpisodeHero episode={ep} eyebrow={formatDate(ep.published_at)} testId="episode" linkTitle={false} />
            {ep.description && ep.description.length > (ep.summary || "").length && (
              <section className="mt-10 grid lg:grid-cols-[1fr_320px] gap-8" data-testid="episode-notes-section">
                <div className="card-surface rounded-3xl p-6 sm:p-8">
                  <p className="eyebrow mb-3">Show notes</p>
                  <p className="whitespace-pre-line text-slate-300 leading-relaxed" data-testid="episode-notes">{ep.description}</p>
                </div>
                <aside className="card-surface rounded-3xl p-6 self-start">
                  <p className="eyebrow mb-3">Spread the word</p>
                  <p className="text-sm text-slate-400 mb-4">Share this {ep.video_id ? "video" : "episode"} with your audience — every listener counts.</p>
                  <ShareButton episode={ep} size="lg" testId="episode-sidebar-share" />
                </aside>
              </section>
            )}
            <RelatedArticles episodeId={ep.id} showSlug={ep.show?.slug} title="The story behind this episode" />
            {ep.more?.length > 0 && (
              <section className="mt-14" data-testid="episode-more-section">
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">More from {ep.show.name}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {ep.more.map((m, i) => <EpisodeCard key={m.id} episode={m} index={i + 1} showLink={false} />)}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
